import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Tag, Row, Col, Modal, Form, Input, Select, message, Spin, Empty } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, BankOutlined, EnvironmentOutlined } from '@ant-design/icons';
import UserServ from '../service/UserServ';
import GlobalHeader from '../util/GlobalHeader';
import { usePreference } from '../context/PreferenceContext';

import GlobalMap from '../util/GlobalMap';

const { Title, Text } = Typography;
const { Option } = Select;

const BuyerAddresses = () => {
    const { t } = usePreference();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [form] = Form.useForm();
    const [mapSearchValue, setMapSearchValue] = useState("");

    const fetchAddresses = async () => {
        try {
            setLoading(true);
            const res = await UserServ.getProfile();
            setAddresses(res.data.address || []);
        } catch (error) {
            message.error(t('error_loading_addresses'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, []);

    const handleAdd = () => {
        setEditingIndex(null);
        form.resetFields();
        setMapSearchValue("");
        setIsModalOpen(true);
    };

    const handleEdit = (addr, index) => {
        setEditingIndex(index);
        form.setFieldsValue({
            ...addr,
            addressType: addr.addressType?.toLowerCase()
        });
        setMapSearchValue(addr.street || addr.address || "");
        setIsModalOpen(true);
    };

    const handleDelete = async (index) => {
        try {
            const newAddresses = addresses.filter((_, i) => i !== index);
            await UserServ.updateProfile({ address: newAddresses });
            message.success(t('address_removed'));
            fetchAddresses();
        } catch (error) {
            message.error(t('error_removing_address'));
        }
    };

    const handleSave = async (values) => {
        try {
            let newAddresses = [...addresses];
            const processedValues = {
                ...values,
                latitude: Number(values.latitude),
                longitude: Number(values.longitude)
            };

            if (editingIndex !== null) {
                newAddresses[editingIndex] = { ...newAddresses[editingIndex], ...processedValues };
            } else {
                newAddresses.push(processedValues);
            }

            // Process data for backend
            newAddresses = newAddresses.map(addr => {
                const cleaned = {
                    ...addr,
                    addressType: addr.addressType?.toLowerCase(),
                    isDefault: addr.isDefault || false
                };

                // If type is not 'other', remove the custom label
                if (cleaned.addressType !== 'other') {
                    delete cleaned.otherAddressType;
                }

                return cleaned;
            });

            await UserServ.updateProfile({ address: newAddresses });
            message.success(editingIndex !== null ? t('address_updated') : t('address_added'));
            setIsModalOpen(false);
            fetchAddresses();
        } catch (error) {
            console.error(error);
            message.error(t('error_saving_address'));
        }
    };

    const fillAddressFields = (data) => {
        form.setFieldsValue({
            street: data.address || "",
            city: data.city || "",
            district: data.district || "",
            state: data.state || "",
            pincode: data.zipCode || "",
            latitude: data.latitude,
            longitude: data.longitude,
        });
        setMapSearchValue(data.address || "");
    };

    // Geocode manually typed address on blur
    const handleManualAddressBlur = () => {
        const street = form.getFieldValue("street");
        const city = form.getFieldValue("city");
        const district = form.getFieldValue("district");
        const pincode = form.getFieldValue("pincode");

        const fullAddr = [street, city, district, pincode].filter(Boolean).join(", ");

        if (fullAddr && window.google) {
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: fullAddr }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const result = results[0];
                    const loc = result.geometry.location;
                    const comps = result.address_components || [];
                    const getComp = (t) => comps.find(c => c.types.includes(t))?.long_name || "";

                    let zipCode = getComp("postal_code");
                    if (!zipCode) {
                        const match = result.formatted_address.match(/\b\d{6}\b/);
                        if (match) zipCode = match[0];
                    }

                    form.setFieldsValue({
                        latitude: loc.lat(),
                        longitude: loc.lng(),
                        city: form.getFieldValue("city") || getComp("locality") || getComp("sublocality"),
                        district: form.getFieldValue("district") || getComp("administrative_area_level_2"),
                        state: form.getFieldValue("state") || getComp("administrative_area_level_1"),
                        pincode: form.getFieldValue("pincode") || zipCode,
                    });
                    setMapSearchValue(result.formatted_address);
                }
            });
        }
    };

    const getIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'home': return <HomeOutlined />;
            case 'office': return <BankOutlined />;
            default: return <EnvironmentOutlined />;
        }
    };

    return (
        <div className="pcu-padding-x-20">
            <GlobalHeader
                title={t('my_addresses')}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                        className="pcu-bg-orange pcu-border-none"
                    >
                        {t('add_new_address_btn')}
                    </Button>
                }
            >

            {loading ? (
                <div className="pcu-text-center pcu-margin-top-50"><Spin size="large" /></div>
            ) : addresses.length === 0 ? (
                <Card><Empty description={t('no_addresses_found')} /></Card>
            ) : (
                <Row gutter={[16, 16]}>
                    {addresses.map((addr, index) => (
                        <Col xs={24} md={12} lg={8} key={index} className="pcu-flex">
                            <Card
                                hoverable
                                className="pcu-w-100 pcu-flex-column pcu-radius-12 pcu-border-muted pcu-overflow-hidden"
                                bodyStyle={{ flex: 1, padding: '20px' }}
                                bodyClassName="pcu-flex-grow-1 pcu-padding-20"
                                actions={[
                                    <div key="edit" onClick={() => handleEdit(addr, index)} className="pcu-padding-y-4"><EditOutlined /> {t('edit')}</div>,
                                    <div key="delete" onClick={() => handleDelete(index)} className="pcu-color-error pcu-padding-y-4"><DeleteOutlined /> {t('delete')}</div>
                                ]}
                            >
                                <Card.Meta
                                    avatar={<div className="pcu-font-24 pcu-color-orange">{getIcon(addr.addressType)}</div>}
                                    title={
                                        <div className="pcu-flex pcu-justify-between pcu-flex-center-y">
                                            <span className="pcu-text-capitalize pcu-color-title pcu-font-600">
                                                {addr.addressType === 'other' ? (addr.otherAddressType || t('other_label')) : t(`${addr.addressType?.toLowerCase()}_label`)}
                                            </span>
                                            {addr.isDefault && <Tag color="success" bordered={false} className="pcu-radius-4">{t('default')}</Tag>}
                                        </div>
                                    }
                                    description={
                                        <div className="pcu-margin-top-12 pcu-min-h-90 pcu-color-body">
                                            <div className="pcu-font-15 pcu-font-500 pcu-margin-bottom-4">{addr.street}</div>
                                            <div className="pcu-font-13 pcu-opacity-80">{addr.city || addr.taluk}, {addr.district}</div>
                                            <div className="pcu-font-13 pcu-opacity-80">{addr.pincode}</div>
                                        </div>
                                    }
                                />
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Modal
                title={editingIndex !== null ? t('edit_address') : t('add_new_address')}
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={() => form.submit()}
                destroyOnClose
                width={700}
                centered
            >
                <div className="pcu-margin-bottom-20">
                    <GlobalMap
                        height="250px"
                        onLocationSelect={fillAddressFields}
                        address={mapSearchValue}
                        markerPosition={
                            form.getFieldValue("latitude") && form.getFieldValue("longitude")
                                ? { lat: parseFloat(form.getFieldValue("latitude")), lng: parseFloat(form.getFieldValue("longitude")) }
                                : null
                        }
                    />
                </div>
                <Form form={form} layout="vertical" onFinish={handleSave}>
                    <Form.Item name="addressType" label={t('address_type')} rules={[{ required: true, message: t('select_addr_type') }]}>
                        <Select placeholder={t('select_addr_type')}>
                            <Option value="home">{t('home_label')}</Option>
                            <Option value="office">{t('office_label')}</Option>
                            <Option value="pickup">{t('pickup_label')}</Option>
                            <Option value="billing">{t('billing_label')}</Option>
                            <Option value="other">{t('other_label')}</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.addressType !== currentValues.addressType}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('addressType') === 'other' ? (
                                <Form.Item
                                    name="otherAddressType"
                                    label={t('label_eg_school')}
                                    rules={[{ required: true, message: t('enter_custom_label') }]}
                                >
                                    <Input placeholder={t('enter_custom_label')} />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item name="street" label={t('street')} rules={[{ required: true, message: t('enter_street') }]}>
                        <Input placeholder={t('enter_street')} onBlur={handleManualAddressBlur} />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="city" label={t('city_taluk')}>
                                <Input placeholder={t('city_taluk')} onBlur={handleManualAddressBlur} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="district" label={t('district')}>
                                <Input placeholder={t('district')} onBlur={handleManualAddressBlur} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="pincode" label={t('pincode')} rules={[{ required: true, message: t('enter_pincode') }]}>
                        <Input placeholder={t('pincode')} onBlur={handleManualAddressBlur} />
                    </Form.Item>

                    <Form.Item name="latitude" noStyle><Input type="hidden" /></Form.Item>
                    <Form.Item name="longitude" noStyle><Input type="hidden" /></Form.Item>
                </Form>
            </Modal>
            </GlobalHeader>
        </div>
    );
};

export default BuyerAddresses;
